import React, { useMemo, useCallback, useState } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ImageStyle,
} from 'react-native';
import { DbProductTitle } from '@/components/DbProductTitle';
import { DbNumberInput } from '@/components/DbNumberInput';
import { DbTag } from '@/components/DbTag';
import DbIcon from '@/components/DbIcon';
import { useDbTheme } from '@/core';
import { createStyles } from './DbProductCard.styles';
import type { DbProductCardProps, ProductPrice } from './DbProductCard.types';

// 解析价格
const parsePrice = (price: ProductPrice | number | string): ProductPrice => {
  if (typeof price === 'number' || typeof price === 'string') {
    return { current: price };
  }
  return price;
};

// 格式化价格
const formatPrice = (value: number | string): string => {
  if (typeof value === 'string') return value;
  return value.toFixed(2).replace(/\.00$/, '');
};

const DbProductCard: React.FC<DbProductCardProps> = ({
  id: _id,
  image,
  title,
  subtitle,
  price,
  tags = [],
  variant = 'vertical',
  salesText,
  rating,
  reviewCount,
  shopName,
  isFavorite = false,
  outOfStock = false,
  outOfStockText = '已售罄',
  badge,
  imageWidth,
  imageHeight,
  imageRadius,
  maxTags = 3,
  showBuyButton = false,
  buyButtonText = '购买',
  showFavoriteButton = false,
  onPress,
  onBuyPress,
  onFavoritePress,
  onImagePress,
  style,
  imageStyle,
  titleStyle,
  priceStyle,
  footer,
  rightExtra,
  // 购物车模式属性
  cartMode: propCartMode,
  titleTags = [],
  titleSuffixTags = [],
  cartTags = [],
  sku,
  onSkuPress,
  quantity = 1,
  onQuantityChange,
  minQuantity = 1,
  maxQuantity = 99,
  showRadio = false,
  selected = false,
  onSelectChange,
  radioDisabled = false,
  showDelete = false,
  onDelete,
  bottomExtra,
  borderRadius,
  gap,
  numberInputSize = 'small',
  numberInputType = 'borderless',
  titleNumberOfLines = 1,
}) => {
  const isCartMode = variant === 'cart' || propCartMode;
  const theme = useDbTheme();
  const styles = useMemo(() => createStyles('light', gap), [gap]);
  const priceInfo = parsePrice(price);
  
  // 从主题获取点击透明度
  const activeOpacity = theme.interaction.activeOpacity;
  const [internalQuantity, setInternalQuantity] = useState(quantity);
  const [showQuantityInput, setShowQuantityInput] = useState(false);

  // 当前数量（优先使用受控模式）
  const currentQuantity = onQuantityChange ? quantity : internalQuantity;

  // 获取容器样式
  const getContainerStyle = useCallback(() => {
    switch (variant) {
      case 'horizontal':
        return styles.horizontalContainer;
      case 'grid':
        return styles.gridContainer;
      case 'list':
        return styles.listContainer;
      case 'cart':
        return styles.cartContainer;
      case 'waterfall':
        return styles.waterfallContainer;
      default:
        return styles.verticalContainer;
    }
  }, [variant, styles]);

  // 获取图片样式
  const getImageStyle = useCallback((): ImageStyle => {
    const baseStyle = (() => {
      switch (variant) {
        case 'horizontal':
          return styles.horizontalImage;
        case 'grid':
          return styles.gridImage;
        case 'list':
          return styles.listImage;
        case 'cart':
          return styles.cartImage;
        case 'waterfall':
          return styles.waterfallImage;
        default:
          return styles.verticalImage;
      }
    })();

    return {
      ...baseStyle,
      ...(imageWidth && { width: imageWidth }),
      ...(imageHeight && { height: imageHeight }),
      ...(imageRadius !== undefined && { borderRadius: imageRadius }),
      ...(borderRadius !== undefined && variant === 'cart' && { borderRadius: borderRadius / 2 }),
    } as ImageStyle;
  }, [variant, styles, imageWidth, imageHeight, imageRadius, borderRadius]);

  // 获取内容样式
  const getContentStyle = useCallback(() => {
    switch (variant) {
      case 'horizontal':
        return styles.horizontalContent;
      case 'grid':
        return styles.gridContent;
      case 'list':
        return styles.listContent;
      case 'cart':
        return styles.cartContent;
      case 'waterfall':
        return styles.waterfallContent;
      default:
        return styles.verticalContent;
    }
  }, [variant, styles]);

  // 渲染价格
  const renderPrice = () => {
    const { current, original, prefix = '¥', suffix, unit } = priceInfo;

    return (
      <View style={styles.priceContainer}>
        <Text style={styles.pricePrefix}>{prefix}</Text>
        <Text style={[styles.priceValue, priceStyle]}>{formatPrice(current)}</Text>
        {suffix && <Text style={styles.priceSuffix}>{suffix}</Text>}
        {unit && <Text style={styles.priceSuffix}>/{unit}</Text>}
        {original && (
          <Text style={styles.originalPrice}>
            ¥{formatPrice(original)}
          </Text>
        )}
      </View>
    );
  };

  // 渲染标签
  const renderTags = () => {
    if (tags.length === 0) return null;
    
    const displayTags = tags.slice(0, maxTags);

    return (
      <View style={styles.tagsContainer}>
        {displayTags.map((tag, index) => (
          <View
            key={index}
            style={[
              styles.tag,
              tag.outline && styles.tagOutline,
              tag.backgroundColor && { backgroundColor: tag.backgroundColor },
              tag.outline && tag.color && { borderColor: tag.color },
            ]}
          >
            <Text
              style={[
                styles.tagText,
                tag.color && { color: tag.color },
              ]}
            >
              {tag.text}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  // 渲染评分
  const renderRating = () => {
    if (rating === undefined) return null;

    const stars = '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));

    return (
      <View style={styles.ratingContainer}>
        <Text style={styles.ratingStar}>{stars}</Text>
        <Text style={styles.ratingText}>{rating.toFixed(1)}</Text>
        {reviewCount !== undefined && (
          <Text style={styles.reviewCount}>({reviewCount})</Text>
        )}
      </View>
    );
  };

  // 渲染销量/评分信息
  const renderInfo = () => {
    if (!salesText && rating === undefined) return null;

    return (
      <View style={styles.infoContainer}>
        {renderRating()}
        {salesText && <Text style={styles.salesText}>{salesText}</Text>}
      </View>
    );
  };

  // 渲染按钮区域
  const renderButtons = () => {
    if (!showBuyButton && !showFavoriteButton) return null;

    return (
      <View style={styles.buttonsContainer}>
        {showFavoriteButton && (
          <TouchableOpacity
            style={styles.favoriteButton}
            onPress={onFavoritePress}
            activeOpacity={activeOpacity}
          >
            <Text
              style={[
                styles.favoriteIcon,
                isFavorite ? styles.favoriteActive : styles.favoriteInactive,
              ]}
            >
              {isFavorite ? '♥' : '♡'}
            </Text>
          </TouchableOpacity>
        )}
        {showBuyButton && (
          <TouchableOpacity
            style={styles.buyButton}
            onPress={onBuyPress}
            activeOpacity={activeOpacity}
          >
            <Text style={styles.buyButtonText}>{buyButtonText}</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // 渲染角标
  const renderBadge = () => {
    if (!badge) return null;

    if (typeof badge === 'string') {
      return (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>{badge}</Text>
        </View>
      );
    }

    return <>{badge}</>;
  };

  // 渲染缺货遮罩
  const renderOutOfStock = () => {
    if (!outOfStock) return null;

    return (
      <View style={styles.outOfStockOverlay}>
        <Text style={styles.outOfStockText}>{outOfStockText}</Text>
      </View>
    );
  };

  // ===== 购物车模式专属渲染 =====
  
  // 渲染选择框
  const renderCheckbox = () => {
    if (!isCartMode || !showRadio) return null;

    const checkboxContent = (
      <View style={[styles.checkbox, selected && styles.checkboxActive]}>
        {selected && (
          <DbIcon name="checkmark" size={12} color="#FFFFFF" />
        )}
      </View>
    );

    // 如果 radioDisabled 为 true，仅展示状态，不可点击
    if (radioDisabled) {
      return (
        <View style={styles.checkboxContainer}>
          {checkboxContent}
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={styles.checkboxContainer}
        onPress={(e) => {
          // 只有当数量输入框显示时才阻止冒泡（避免关闭输入框）
          if (showQuantityInput) {
            e.stopPropagation();
          }
          onSelectChange?.(!selected);
        }}
        activeOpacity={activeOpacity}
      >
        {checkboxContent}
      </TouchableOpacity>
    );
  };

  // 渲染购物车标签
  const renderCartTags = () => {
    if (cartTags.length === 0) return null;

    return (
      <View style={[styles.cartTagsContainer, styles.gapXs]}>
        {cartTags.map((tag, index) => (
          <DbTag
            key={index}
            text={tag.text}
            preset={tag.preset || 'guarantee'}
            variant={tag.variant || 'light'}
            size="mini"
            shape="rounded"
            color={tag.color}
            backgroundColor={tag.backgroundColor}
            borderColor={tag.borderColor}
            textColor={tag.textColor}
          />
        ))}
      </View>
    );
  };

  // 渲染 SKU 选择器
  const renderSku = () => {
    if (!sku) return null;

    return (
      <TouchableOpacity
        style={[styles.skuContainer, styles.gapXs]}
        onPress={(e) => {
          // 只有当数量输入框显示时才阻止冒泡（避免关闭输入框）
          if (showQuantityInput) {
            e.stopPropagation();
          }
          onSkuPress?.();
        }}
        activeOpacity={activeOpacity}
      >
        <Text style={styles.skuText} numberOfLines={1}>
          {sku.name}
        </Text>
        <DbIcon
          name="chevron-forward"
          size={14}
          color="#999999"
          style={styles.skuArrow}
        />
      </TouchableOpacity>
    );
  };

  // 处理数量变化
  const handleQuantityChange = (newQuantity: number) => {
    if (!onQuantityChange) {
      setInternalQuantity(newQuantity);
    }
    onQuantityChange?.(newQuantity);
  };

  // 渲染数量选择器（文本或输入框）
  const renderQuantitySelector = () => {
    if (showQuantityInput) {
      return (
        <TouchableOpacity
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          <DbNumberInput
            initialValue={currentQuantity}
            min={minQuantity}
            max={maxQuantity}
            onChange={handleQuantityChange}
            size={numberInputSize}
            type={numberInputType}
          />
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        onPress={(e) => {
          e.stopPropagation();
          setShowQuantityInput(true);
        }}
        activeOpacity={activeOpacity}
        style={styles.quantityTextContainer}
      >
        <Text style={styles.quantityText}>x{currentQuantity}</Text>
      </TouchableOpacity>
    );
  };

  // 渲染删除按钮
  const renderDeleteButton = () => {
    if (!showDelete) return null;

    return (
      <TouchableOpacity
        style={styles.deleteButton}
        onPress={(e) => {
          e.stopPropagation();
          onDelete?.();
        }}
        activeOpacity={0.7}
      >
        <DbIcon name="close" size={20} color="#999999" />
      </TouchableOpacity>
    );
  };

  // 渲染购物车图片区域
  const renderCartImage = () => (
    <TouchableOpacity
      activeOpacity={1}
      onPress={(e) => {
        // 点击图片关闭数量输入框
        if (showQuantityInput) {
          setShowQuantityInput(false);
        }
      }}
      style={styles.cartImageSection}
    >
      <Image source={{ uri: image }} style={[getImageStyle(), imageStyle]} />
      {renderBadge()}
      {renderOutOfStock()}
    </TouchableOpacity>
  );

  // 渲染购物车内容区域
  const renderCartContent = () => (
    <TouchableOpacity
      activeOpacity={1}
      onPress={() => {
        // 点击内容区域关闭数量输入框
        if (showQuantityInput) {
          setShowQuantityInput(false);
        }
      }}
      style={styles.cartContent}
    >
      {/* 顶部：标题 */}
      <View style={styles.cartTopSection}>
        <DbProductTitle.Inline
          title={title}
          inlineTags={titleTags}
          numberOfLines={titleNumberOfLines}
          fontSize={16}
          color="#333333"
        />
        {renderCartTags()}
      </View>

      {/* 中部：SKU 选择器 */}
      {renderSku()}

      {/* 底部：价格和数量 */}
      <View style={[styles.cartBottomSection, styles.gapMd]}>
        {renderPrice()}
        {renderQuantitySelector()}
      </View>

      {/* 底部扩展区域 */}
      {bottomExtra && (
        <View style={styles.cartBottomExtra}>
          {bottomExtra}
        </View>
      )}
    </TouchableOpacity>
  );

  // 处理购物车容器点击（点击外部关闭数量选择器）
  const handleCartContainerPress = () => {
    if (showQuantityInput) {
      setShowQuantityInput(false);
    }
  };

  // 渲染购物车模式
  const renderCartMode = () => {
    return (
      <TouchableOpacity
        activeOpacity={1}
        onPress={handleCartContainerPress}
        style={[
          styles.cartContainer,
          borderRadius !== undefined && { borderRadius },
          style,
        ]}
      >
        {renderCheckbox()}
        {renderCartImage()}
        {renderCartContent()}
        {/* {renderDeleteButton()} */}
      </TouchableOpacity>
    );
  };

  // 渲染图片区域
  const renderImage = () => (
    <TouchableOpacity
      activeOpacity={onImagePress ? 0.7 : 1}
      onPress={onImagePress}
    >
      <Image source={{ uri: image }} style={[getImageStyle(), imageStyle]} />
      {renderBadge()}
      {renderOutOfStock()}
    </TouchableOpacity>
  );

  // 渲染内容区域
  const renderContent = () => (
    <View style={getContentStyle()}>
      <Text
        style={[
          styles.title,
          (variant === 'vertical' || variant === 'grid' || variant === 'waterfall') && styles.titleTwoLines,
          titleStyle,
        ]}
        numberOfLines={2}
      >
        {title}
      </Text>
      
      {subtitle && (
        <Text style={[styles.subtitle, styles.gapXs]} numberOfLines={1}>
          {subtitle}
        </Text>
      )}

      {renderTags()}
      {renderPrice()}
      {renderInfo()}
      
      {shopName && <Text style={[styles.shopName, styles.gapXs]}>{shopName}</Text>}
      
      {renderButtons()}
      
      {footer && <View style={styles.footerContainer}>{footer}</View>}
    </View>
  );

  // 购物车模式
  if (isCartMode) {
    return renderCartMode();
  }

  // 水平布局
  if (variant === 'horizontal' || variant === 'list') {
    return (
      <TouchableOpacity
        style={[getContainerStyle(), style]}
        onPress={onPress}
        activeOpacity={activeOpacity}
      >
        {renderImage()}
        {renderContent()}
        {rightExtra && (
          <View style={styles.rightExtraContainer}>{rightExtra}</View>
        )}
      </TouchableOpacity>
    );
  }

  // 垂直布局 (包括 vertical, grid, waterfall)
  return (
    <TouchableOpacity
      style={[getContainerStyle(), style]}
      onPress={onPress}
      activeOpacity={activeOpacity}
    >
      {renderImage()}
      {renderContent()}
    </TouchableOpacity>
  );
};

export default DbProductCard;

// 导出瀑布流商品卡片组件
export { DbWaterfallProductCard } from './DbWaterfallProductCard';
export type { DbWaterfallProductCardProps } from './DbWaterfallProductCard';
